import { computed, unref } from 'vue'
import { getTemplateBusinessType } from '@/constants/templates'

/**
 * Labels for the public "view list" button and menu modal that depend on business_type.
 * When restaurant.business_type is not set, derives from restaurant.template (template tags).
 * @param {import('vue').Ref<object>|object} restaurantRef - Restaurant object or ref (may have business_type and/or template)
 * @param {(key: string) => string} t - i18n t function (e.g. from useI18n), used with public.* keys
 * @returns {{ viewListLabel: import('vue').ComputedRef<string>, viewListAriaLabel: import('vue').ComputedRef<string>, modalTitle: import('vue').ComputedRef<string>, closeModalAriaLabel: import('vue').ComputedRef<string>, sectionTitle: import('vue').ComputedRef<string>, emptyMessage: import('vue').ComputedRef<string> }}
 */
/** Resolve restaurant from a ref, getter function, or plain object. */
function resolveRestaurant(restaurantRef) {
  if (typeof restaurantRef === 'function') return restaurantRef()
  return unref(restaurantRef)
}

export function useBusinessTypeLabels(restaurantRef, t) {
  /** Effective business type: restaurant's setting, or derived from template tag, or 'restaurants'. */
  const businessType = computed(() => {
    const r = resolveRestaurant(restaurantRef)
    const bt = r?.business_type
    if (bt) return bt
    const template = r?.template
    return getTemplateBusinessType(template) || 'restaurants'
  })

  const viewListLabel = computed(() => {
    const bt = businessType.value
    const r = resolveRestaurant(restaurantRef)
    const template = r?.template
    if (bt === 'ecommerce') return t('public.viewCatalog')
    if (bt === 'services') {
      if (template === 'template-2') return t('public.t2ViewServices')
      return t('public.viewServices')
    }
    if (bt === 'general') return t('public.viewCatalog')
    return t('public.viewMenu')
  })

  /** Short nav label by business type: "Menu" | "Services" | "Products". */
  const navLabel = computed(() => {
    const bt = businessType.value
    if (bt === 'ecommerce') return t('public.navProducts')
    if (bt === 'services') return t('public.navServices')
    if (bt === 'general') return t('public.navProducts')
    return t('public.navMenu')
  })

  const viewListAriaLabel = computed(() => {
    const bt = businessType.value
    if (bt === 'ecommerce') return t('public.ariaViewCatalog')
    if (bt === 'services') return t('public.ariaViewServices')
    if (bt === 'general') return t('public.ariaViewCatalog')
    return t('public.ariaViewMenu')
  })

  /** Plain "View Services" / "View Menu" / "View Catalog" for buttons (no "01." prefix). Use for sticky bar, etc. */
  const viewListButtonLabel = computed(() => {
    const bt = businessType.value
    if (bt === 'ecommerce') return t('public.viewCatalog')
    if (bt === 'services') return t('public.viewServices')
    if (bt === 'general') return t('public.viewCatalog')
    return t('public.viewMenu')
  })

  const modalTitle = computed(() => {
    const bt = businessType.value
    if (bt === 'ecommerce') return t('public.modalTitleProducts')
    if (bt === 'services') return t('public.modalTitleServices')
    if (bt === 'general') return t('public.modalTitleCatalog')
    return t('public.modalTitleMenu')
  })

  /** Section heading for the menu/products/services block (e.g. "Our Menu", "Our Catalog", "Services"). */
  const sectionTitle = computed(() => {
    const bt = businessType.value
    if (bt === 'ecommerce') return t('public.ourProducts')
    if (bt === 'services') return t('public.ourServices')
    if (bt === 'general') return t('public.ourCatalog')
    return t('public.ourMenu')
  })

  /** Empty-state message when there are no items (e.g. "No menu items", "No products", "No services"). */
  const emptyMessage = computed(() => {
    const bt = businessType.value
    if (bt === 'ecommerce') return t('public.noProducts')
    if (bt === 'services') return t('public.noServices')
    if (bt === 'general') return t('public.noCatalogItems')
    return t('public.noMenuItems')
  })

  const closeModalAriaLabel = computed(() => {
    const bt = businessType.value
    if (bt === 'ecommerce') return t('public.closeCatalogModal')
    if (bt === 'services') return t('public.closeServicesModal')
    if (bt === 'general') return t('public.closeCatalogModal')
    return t('public.closeMenu')
  })

  /** Reservation CTA button label (e.g. "Book a table" for restaurants, "Book an appointment" for services). */
  const reserveButtonLabel = computed(() => {
    const bt = businessType.value
    if (bt === 'services') return t('public.bookAppointment')
    return t('reservation.reserveButton')
  })

  /** Reservation modal title/heading (e.g. "Book a table" for restaurants, "Book" for services). */
  const reserveModalHeading = computed(() => {
    const bt = businessType.value
    if (bt === 'services') return t('reservation.headingServices')
    return t('reservation.heading')
  })

  /** Reservation form "pax" field label (e.g. "Number of guests" for restaurants, "Party size" for services). */
  const reservePaxLabel = computed(() => {
    const bt = businessType.value
    if (bt === 'services') return t('reservation.paxServices')
    return t('reservation.pax')
  })

  /** Default footer tagline by business type (e.g. dining for restaurants, products for ecommerce, service for services). */
  const footerTagline = computed(() => {
    const bt = businessType.value
    if (bt === 'ecommerce') return t('public.t3FooterTaglineEcommerce')
    if (bt === 'services') return t('public.t3FooterTaglineServices')
    if (bt === 'general') return t('public.t3FooterTaglineEcommerce')
    return t('public.t3FooterTaglineRestaurants')
  })

  return {
    viewListLabel,
    navLabel,
    viewListAriaLabel,
    viewListButtonLabel,
    modalTitle,
    closeModalAriaLabel,
    sectionTitle,
    emptyMessage,
    reserveButtonLabel,
    reserveModalHeading,
    reservePaxLabel,
    footerTagline,
  }
}
